import { useCallback } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Stack, Text } from "@similarweb/ui-components/foundations";

import { EMPTY_ARRAY } from "app/pages/sales-intelligence/constants/common";
import type { IFilterInstance } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import { ScoringBoardContainer } from "app/pages/sales-intelligence/modules/lead-scoring-agent/containers/ScoringBoardContainer";
import type { LeadsSearchAllFiltersTypes } from "app/pages/sales-intelligence/modules/leads-search-filters/types/leadsSearchFilters.dto";
import { useScoringRuleUsageByPlaybook } from "app/pages/sales-intelligence/modules/playbooks/hooks/useScoringRuleUsageByPlaybook";
import { getPlaybookScoringConfigType } from "app/pages/sales-intelligence/modules/playbooks/utils/getPlaybookScoringConfigType";
import type { IScoringRuleDto } from "app/pages/sales-intelligence/sub-modules/signals/types/scoring-config";

import { PlaybookScoringBoardContainer } from "./PlaybookScoringBoardContainer";

type PlaybookScoreCriteriaStepProps = {
    isSharedWithAccount: boolean;
    scoringRuleId: string | null;
    onScoringRuleChange: (ruleId: string) => void;
    filtersList: readonly IFilterInstance<LeadsSearchAllFiltersTypes>[];
    isBootstrapping: boolean;
    currentPlaybookId?: number;
};

export const PlaybookScoreCriteriaStep = ({
    isSharedWithAccount,
    scoringRuleId,
    onScoringRuleChange,
    filtersList,
    isBootstrapping,
    currentPlaybookId,
}: PlaybookScoreCriteriaStepProps) => {
    const { translate } = useTranslate();
    const usageMap = useScoringRuleUsageByPlaybook(currentPlaybookId);

    const getRuleUsage = useCallback(
        (rule: IScoringRuleDto) => (rule.id ? (usageMap.get(rule.id) ?? EMPTY_ARRAY) : EMPTY_ARRAY),
        [usageMap],
    );

    return (
        <Stack
            gap="24px"
            data-automation="playbook-wizard-score-step"
        >
            <Text
                fontVariant="body1"
                color={colorsPalettes.carbon["400"]}
                data-automation="playbook-wizard-score-step-subtitle"
            >
                {translate("si.playbooks.wizard.score_criteria.subtitle")}
            </Text>
            {isSharedWithAccount ? (
                <ScoringBoardContainer
                    configType={getPlaybookScoringConfigType(isSharedWithAccount)}
                    filtersList={filtersList}
                    isBootstrapping={isBootstrapping}
                    selectedRuleId={scoringRuleId}
                    onSelect={onScoringRuleChange}
                    getRuleUsage={getRuleUsage}
                    automationPrefix="playbook-scoring-board"
                />
            ) : (
                <PlaybookScoringBoardContainer
                    filtersList={filtersList}
                    isBootstrapping={isBootstrapping}
                    selectedRuleId={scoringRuleId}
                    onSelect={onScoringRuleChange}
                    getRuleUsage={getRuleUsage}
                />
            )}
        </Stack>
    );
};
